import PropTypes from 'prop-types';
import './style.scss';

import { useMemo, useEffect } from 'react';

import { useForm, useFormState } from 'react-hook-form';
import { Button } from 'primereact/button';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

import { useTranslation } from 'react-i18next';
// import { date } from 'yup';
import createProjectValidationSchema from 'features/SubmitProjectProposal/validation';
import { getGeneralInfoById } from 'apis/projectProposals.api';
import {
  createContract,
  uploadContractFiles,
  deleteContractFiles,
  getProjectByProjectProposalId,
} from 'apis/projects.api';
import { TOAST } from 'constant';
import { toast } from 'layout';
import { mapError, convertToPascalCase, convertUnicodeToAscii } from 'utils/func';

import Loading from 'components/Loading';
import {
  defaultCreateContractValues,
  fieldsInputForm,
} from 'features/SubmitProjectProposal/defaultValues';
import Form from '../../components/Form';

const formOptions = { resolver: yupResolver(createProjectValidationSchema) };

function CreateContract({ disabled }) {
  // #region Data
  const { t } = useTranslation();
  const { projectProposalId } = useParams();

  const { control, handleSubmit, watch, setValue, getValues, reset, resetField } = useForm({
    mode: 'onChange',
    ...formOptions,
    shouldFocusError: true,
    defaultValues: defaultCreateContractValues,
  });
  const { errors, isDirty, dirtyFields, isValid } = useFormState({
    control,
  });

  const {
    data: _data,
    isLoading,
    isError,
    error: errorProject,
  } = useQuery(['generalInfoProject'], () => getGeneralInfoById(projectProposalId));
  const contractInfo = useMemo(() => _data?.data ?? [], [_data]);
  const owner = useMemo(
    () =>
      contractInfo?.projectProposalContacts?.find((item) => item?.projectRole?.code === 'OWNER'),
    [contractInfo]
  );
  const {
    data: projectData,
    isLoading: isProjectDataLoading,
    refetch,
  } = useQuery(['project', projectProposalId], () =>
    getProjectByProjectProposalId(projectProposalId)
  );
  const project = useMemo(() => projectData?.data, [projectData]);
  const projectId = useMemo(() => project?.id ?? null, [project]);

  const { mutate: createMutate, isLoading: isCreateLoading } = useMutation(createContract);

  const { mutate: uploadMutate, isLoading: isUploadLoading } = useMutation(uploadContractFiles);
  const { mutate: deleteContractFilesMutate, isLoading: isDeleteContractFilesMutate } =
    useMutation(deleteContractFiles);

  const onSubmit = async (data) => {
    const newData = { ...data };

    Object.keys(newData).forEach((key) => {
      if (key.toLowerCase().includes('expenditure')) {
        newData[key] = parseInt(newData[key], 10);
      }
    });

    const dataSender = {
      projectProposalId: projectProposalId ?? '0',
      ...newData,
    };

    delete dataSender?.appendixFiles;
    delete dataSender?.projectAppendixIds;
    delete dataSender?.fileUrl;
    delete dataSender?.numberMonthImplementation;

    const formContractFileData = new FormData();
    const contractFileName = convertUnicodeToAscii(
      `${(dataSender.projectCode ?? '').replace(/\//g, '-')}-${
        dataSender.fullNameOwner
      }-HopDong.pdf`
    );

    formContractFileData.append('projectProposalId', projectProposalId);

    if (data?.fileUrl && data?.fileUrl instanceof File) {
      const contactFile = new File([data?.fileUrl], contractFileName, {
        type: 'application/pdf',
      });

      formContractFileData.append('projectDecisionFile', contactFile);
    }

    data.appendixFiles.forEach((file) => {
      formContractFileData.append('appendixFiles', file);
    });

    const formDeleteContractFileData = {};
    formDeleteContractFileData.projectProposalId = projectProposalId;
    formDeleteContractFileData.projectAppendixIds = data.projectAppendixDeletedFileIds;
    const isFieldsForm = fieldsInputForm.some((key) => Object.keys(dirtyFields).includes(key));

    createMutate(dataSender, {
      onSuccess: () => {
        if (
          Object.keys(dirtyFields).includes('fileUrl') ||
          data.appendixFiles.length > 0 ||
          data.projectAppendixDeletedFileIds.length > 0
        ) {
          uploadMutate(formContractFileData, {
            onSuccess: () => {
              if (data.projectAppendixDeletedFileIds.length > 0) {
                deleteContractFilesMutate(formDeleteContractFileData, {
                  onSuccess() {
                    toast(TOAST.SUCCESS, t('notification.updateFile.success'));
                    if (isFieldsForm) {
                      reset();
                    } else {
                      resetField('appendixFiles');
                      resetField('projectAppendixDeletedFileIds');
                    }
                    refetch();
                  },
                  onError: (error) => {
                    toast(TOAST.ERROR, mapError(t, error));
                  },
                });
              } else {
                toast(TOAST.SUCCESS, t('notification.uploadFile.success'));
                reset();
                refetch();
              }
            },
            onError: (error) => {
              toast(TOAST.ERROR, mapError(t, error));
            },
          });
        } else {
          toast(TOAST.SUCCESS, t('notification.updateContract.success'));
          reset();
          refetch();
        }
      },
      onError: (error) => {
        toast(TOAST.ERROR, mapError(t, error));
      },
    });
  };

  const checkValidation = () => {
    if (!isValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };
  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('projectProposal.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />

      <Button
        label={t('projectProposal.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );

  useEffect(() => {
    setValue('totalExpenditure', project?.totalExpenditure);
    setValue('expenditureFromVNU', project?.expenditureFromVNU);
    setValue('fixedExpenditure', project?.fixedExpenditure);
    setValue('flexibleExpenditure', project?.flexibleExpenditure);
    setValue('expenditureFromFunding', project?.expenditureFromFunding);
    setValue('projectCode', project?.projectCode);
    setValue('topicCode', project?.topicCode);
    setValue('signingDate', project?.signingDate ? new Date(project?.signingDate) : null);
    setValue('startDate', project?.startDate ? new Date(project?.startDate) : null);
    setValue('endDate', project?.endDate ? new Date(project?.endDate) : null);
    setValue('fileUrl', project?.fileUrl);
    setValue('projectAppendixes', project?.projectAppendixes);
    if (owner) {
      setValue('fullNameOwner', convertToPascalCase(owner?.fullname));
    }
  }, [project, contractInfo]);

  if (isLoading || isProjectDataLoading) {
    return <Loading />;
  }

  if (isError) {
    return <h4 className="text-red-500">{mapError(t, errorProject)}</h4>;
  }

  return (
    <div className="tab-view-create-contract">
      <Form
        control={control}
        errors={errors}
        watch={watch}
        reset={reset}
        refetch={refetch}
        setValue={setValue}
        getValues={getValues}
        handleSubmit={handleSubmit(onSubmit)}
        checkValidation={checkValidation}
        checkKeyDown={checkKeyDown}
        isDirty={isDirty}
        dirtyFields={dirtyFields}
        footerConfirmationPrompt={footerConfirmationPrompt}
        projectProposalId={projectProposalId}
        totalExpenditurePP={contractInfo?.totalExpenditure}
        disabled={disabled}
        isSendMailToOwner={project?.isSentEmail}
        projectId={projectId}
        hasContractFile={!project?.fileUrl}
        isCreateLoading={isCreateLoading}
        projectType={contractInfo?.projectType}
      />
      {(isCreateLoading || isUploadLoading || isDeleteContractFilesMutate) && <Loading />}
    </div>
  );
}

CreateContract.propTypes = {
  disabled: PropTypes.bool,
};

CreateContract.defaultProps = { disabled: false };

export default CreateContract;
